import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Plane, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import DestinationAutocomplete from '../components/DestinationAutocomplete';
import BookingConfirmation from '../components/BookingConfirmation';
import BookingModal from '../components/BookingModal';

const API = "https://travel.idesign4u.in/api";

export default function FlightsPage() {
  const [searchData, setSearchData] = useState({
    from_city: '',
    to_city: '',
    departure_date: '',
    return_date: '',
    passengers: 1
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

const handleSearch = (e) => {
  e.preventDefault();

  if (!searchData.from_city || !searchData.to_city || !searchData.departure_date) {
    toast.error("Please fill all required fields");
    return;
  }

  const airportCodes = {
    hyderabad: "HYD",
    dubai: "DXB",
    delhi: "DEL",
    mumbai: "BOM",
    bangalore: "BLR",
    chennai: "MAA",
    kolkata: "CCU",
    pune: "PNQ"
  };

  const from = airportCodes[searchData.from_city.toLowerCase()] || searchData.from_city;
  const to = airportCodes[searchData.to_city.toLowerCase()] || searchData.to_city;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const depart = formatDate(searchData.departure_date);

  let url;

  if (searchData.return_date) {
    const ret = formatDate(searchData.return_date);

    url = `https://www.makemytrip.com/flight/search/?tripType=R&itinerary=${from}-${to}-${depart}_${to}-${from}-${ret}&paxType=A-1_C-0_I-0&cabinClass=E`;
  } else {
    url = `https://www.makemytrip.com/flight/search/?tripType=O&itinerary=${from}-${to}-${depart}&paxType=A-1_C-0_I-0&cabinClass=E`;
  }

  window.open(url, "_blank");
};





  const handleBookingSubmit = async (customerData) => {
    if (!selectedFlight) return;

    try {
      setBookingLoading(true);
      const res = await axios.post(`${API}/bookings/create`, {
        booking_type: 'flight',
        customer_name: customerData.name,
        customer_mobile: customerData.mobile,
        customer_email: customerData.email || '',
        from_location: selectedFlight.from_city,
        to_location: selectedFlight.to_city,
        travel_date: searchData.departure_date,
        travelers: searchData.passengers,
        provider_name: selectedFlight.airline,
        departure_time: selectedFlight.departure_time,
        arrival_time: selectedFlight.arrival_time,
        duration: selectedFlight.duration,
        class_type: selectedFlight.class,
        price: selectedFlight.price * searchData.passengers
      });
      
      setShowBookingModal(false);
      setBookingDetails({
        bookingId: res.data.booking_id,
        name: `${selectedFlight.airline} - ${selectedFlight.from_city} to ${selectedFlight.to_city}`,
        details: `${selectedFlight.departure_time} → ${selectedFlight.arrival_time} (${selectedFlight.duration})`,
        date: searchData.departure_date,
        travelers: searchData.passengers,
        price: selectedFlight.price * searchData.passengers
      });
      setShowConfirmation(true);
    } catch (err) {
      toast.error('Error creating booking');
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-20" data-testid="flights-page">
      {/* Hero */}
      <section className="relative h-64 md:h-80 bg-gradient-to-r from-blue-600 to-blue-800 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920"
            alt="Flights"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-4"
          >
            <Plane className="w-12 h-12 text-white" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-medium text-white mb-4"
          >
            Book Flights
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-blue-100"
          >
            Find the best deals on domestic & international flights
          </motion.p>
        </div>
      </section>

      {/* Search Form */}
      <section className="max-w-5xl mx-auto px-6 -mt-12 relative z-20">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSearch}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
          data-testid="flight-search-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <DestinationAutocomplete
              label="From"
              value={searchData.from_city}
              onChange={(val) => setSearchData({ ...searchData, from_city: val })}
              placeholder="Delhi"
            />
            <DestinationAutocomplete
              label="To"
              value={searchData.to_city}
              onChange={(val) => setSearchData({ ...searchData, to_city: val })}
              placeholder="Mumbai"
            />
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Departure</label>
              <input
                type="date"
                value={searchData.departure_date}
                onChange={(e) => setSearchData({ ...searchData, departure_date: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                data-testid="departure-date"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Return (Optional)</label>
              <input
                type="date"
                value={searchData.return_date}
                onChange={(e) => setSearchData({ ...searchData, return_date: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                data-testid="return-date"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Passengers</label>
              <select
                value={searchData.passengers}
                onChange={(e) => setSearchData({ ...searchData, passengers: parseInt(e.target.value) })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none bg-white"
                data-testid="passengers"
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? 'Passenger' : 'Passengers'}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-center">
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-12 py-6 text-lg font-medium"
              data-testid="search-flights-btn"
            >
              {loading ? 'Searching...' : 'Search Flights'}
            </Button>
          </div>
        </motion.form>
      </section>

      {/* Discount Banner */}
      <section className="max-w-5xl mx-auto px-6 mt-8">
        <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            <span className="font-semibold">Special Discount Available!</span> Book through ID4U Travels and get exclusive discounts on all flights.
          </p>
        </div>
      </section>

      {/* Results */}
      {searched && (
        <section className="max-w-5xl mx-auto px-6 py-12" data-testid="flight-results">
          <h2 className="text-2xl font-semibold text-slate-900 mb-6">
            {results.length > 0 ? `${results.length} Flights Found` : 'No Flights Found'}
          </h2>
          <div className="space-y-4">
            {results.map((flight, index) => (
              <motion.div
                key={flight.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-slate-100"
                data-testid={`flight-result-${index}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <Plane className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{flight.airline}</h3>
                      <p className="text-sm text-slate-500">{flight.class}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-8">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{flight.departure_time}</p>
                      <p className="text-sm text-slate-500">{flight.from_city}</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <p className="text-xs text-slate-400 mb-1">{flight.duration}</p>
                      <div className="w-24 h-px bg-slate-300 relative">
                        <Plane className="w-4 h-4 text-blue-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-90" />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{flight.stops === 0 ? 'Non-stop' : `${flight.stops} Stop(s)`}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-900">{flight.arrival_time}</p>
                      <p className="text-sm text-slate-500">{flight.to_city}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">₹{flight.price?.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">per person</p>
                    </div>
                    <Button
                      onClick={() => openBookingModal(flight)}
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                      data-testid={`book-flight-${index}`}
                    >
                      Book Now
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Booking Modal */}
      <AnimatePresence>
        {showBookingModal && (
          <BookingModal
            isOpen={showBookingModal}
            onClose={() => setShowBookingModal(false)}
            onSubmit={handleBookingSubmit}
            title="Book Your Flight"
            loading={bookingLoading}
          />
        )}
      </AnimatePresence>

      {/* Booking Confirmation Modal */}
      <AnimatePresence>
        {showConfirmation && (
          <BookingConfirmation
            bookingType="flight"
            bookingDetails={bookingDetails}
            onClose={() => setShowConfirmation(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
